import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
    ArrowLeft,
    Loader2,
    Star,
    Globe2,
    Scale,
    Award,
    HandHeart,
    CheckCircle2,
    Mail,
    Phone,
} from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import {
    getCase,
    getCaseMatches,
    createReferral,
    getMeta,
    getStoredEmail,
} from "@/lib/api";

const TIER_LABEL = {
    starter: "STARTER",
    professional: "PROFESSIONAL",
    elite: "ELITE",
    enterprise: "ENTERPRISE",
};

const TIER_STYLE = {
    starter: "border-white/40 text-white/70",
    professional: "border-white text-white",
    elite: "bg-[#FF3B30] border-[#FF3B30] text-white",
    enterprise: "bg-white text-black border-white",
};

const Stars = ({ n }) => (
    <div className="flex gap-0.5">
        {Array.from({ length: 5 }).map((_, i) => (
            <Star
                key={i}
                size={12}
                className={i < n ? "fill-[#FFCC00] text-[#FFCC00]" : "text-white/20"}
            />
        ))}
    </div>
);

const AttorneyCard = ({ match, onRequest, requesting, requested }) => {
    const a = match.attorney;
    return (
        <div
            className="border-2 border-white/20 p-6 hover:bg-white/5 transition-colors"
            data-testid={`attorney-card-${a.attorney_id}`}
        >
            <div className="flex items-start justify-between gap-4 mb-4">
                <div>
                    <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/50 mb-1">
                        {a.law_firm}
                    </div>
                    <div className="font-anton text-2xl uppercase leading-tight">{a.name}</div>
                    <div className="mt-2 flex items-center gap-3">
                        <Stars n={a.rating} />
                        <span className="font-mono text-[10px] uppercase tracking-wider text-white/50">
                            {a.cases_won} cases won
                        </span>
                    </div>
                </div>
                <div className="text-right flex flex-col items-end gap-2">
                    <div className="font-anton text-4xl text-[#FF3B30] leading-none">
                        {match.match_score}
                        <span className="text-white/40 text-xs ml-1">%</span>
                    </div>
                    <span
                        className={`inline-block font-mono text-[9px] uppercase tracking-[0.25em] px-2 py-1 border-2 ${TIER_STYLE[a.subscription_tier] || ""}`}
                    >
                        {TIER_LABEL[a.subscription_tier] || a.subscription_tier}
                    </span>
                </div>
            </div>

            <p className="font-sans text-sm text-white/80 leading-relaxed mb-4">{a.bio}</p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4 text-xs font-mono">
                <div className="flex items-start gap-2 text-white/70">
                    <Scale size={14} className="mt-0.5 text-[#FF3B30] flex-shrink-0" />
                    <span className="uppercase tracking-wider">
                        {(a.specialities || []).slice(0, 3).join(" • ").replace(/_/g, " ")}
                    </span>
                </div>
                <div className="flex items-start gap-2 text-white/70">
                    <Globe2 size={14} className="mt-0.5 text-[#FF3B30] flex-shrink-0" />
                    <span className="uppercase tracking-wider">
                        {(a.languages || []).join(" / ")}
                    </span>
                </div>
                <div className="flex items-start gap-2 text-white/70">
                    <Award size={14} className="mt-0.5 text-[#FF3B30] flex-shrink-0" />
                    <span className="uppercase tracking-wider">
                        {(a.jurisdictions || []).join(" • ")}
                    </span>
                </div>
                {a.accepts_pro_bono && (
                    <div className="flex items-center gap-2 text-white">
                        <HandHeart size={14} className="text-[#FFCC00] flex-shrink-0" />
                        <span className="uppercase tracking-wider font-bold">PRO-BONO ACCEPTED</span>
                    </div>
                )}
            </div>

            {match.match_reasons?.length > 0 && (
                <div className="border-t border-white/10 pt-3 mb-4">
                    <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/50 mb-2">
                        WHY THIS MATCH
                    </div>
                    <ul className="space-y-1">
                        {match.match_reasons.map((r, i) => (
                            <li
                                key={i}
                                className="font-sans text-xs text-white/80 flex gap-2"
                            >
                                <span className="text-[#FF3B30]">→</span>
                                <span>{r}</span>
                            </li>
                        ))}
                    </ul>
                </div>
            )}

            {requested ? (
                <div
                    className="flex items-center gap-2 font-mono text-xs uppercase tracking-[0.25em] text-white border-2 border-white px-4 py-3"
                    data-testid={`attorney-requested-${a.attorney_id}`}
                >
                    <CheckCircle2 size={14} /> CONSULTATION REQUESTED
                </div>
            ) : (
                <button
                    onClick={() => onRequest(a)}
                    disabled={requesting}
                    className="btn-brutal btn-brutal-red w-full justify-center"
                    data-testid={`request-consult-${a.attorney_id}`}
                >
                    {requesting ? (
                        <>
                            <Loader2 size={14} className="animate-spin" /> SENDING...
                        </>
                    ) : (
                        <>REQUEST CONSULTATION</>
                    )}
                </button>
            )}
        </div>
    );
};

export default function AttorneyEscalation() {
    const { caseId } = useParams();
    const nav = useNavigate();
    const [meta, setMeta] = useState(null);
    const [caseData, setCaseData] = useState(null);
    const [matches, setMatches] = useState([]);
    const [loading, setLoading] = useState(true);

    const [showForm, setShowForm] = useState(false);
    const [activeAttorney, setActiveAttorney] = useState(null);
    const [requesting, setRequesting] = useState(false);
    const [requested, setRequested] = useState({}); // attorney_id -> true

    const [form, setForm] = useState({
        user_name: "",
        user_email: getStoredEmail(),
        user_phone: "",
        notes: "",
    });

    useEffect(() => {
        getMeta().then(setMeta).catch(() => {});
        Promise.all([getCase(caseId), getCaseMatches(caseId, 5)])
            .then(([c, m]) => {
                setCaseData(c);
                setMatches(m.matches || []);
            })
            .catch(() => toast.error("Could not load matches"))
            .finally(() => setLoading(false));
    }, [caseId]);

    const startRequest = (attorney) => {
        setActiveAttorney(attorney);
        setShowForm(true);
    };

    const submitRequest = async () => {
        if (!activeAttorney) return;
        setRequesting(true);
        try {
            await createReferral({
                case_id: caseId,
                attorney_id: activeAttorney.attorney_id,
                user_name: form.user_name,
                user_email: form.user_email,
                user_phone: form.user_phone,
                notes: form.notes,
            });
            setRequested((r) => ({ ...r, [activeAttorney.attorney_id]: true }));
            setShowForm(false);
            setActiveAttorney(null);
            toast.success(`Consultation requested with ${activeAttorney.name}`);
        } catch (e) {
            toast.error("Request failed. Please try again.");
        } finally {
            setRequesting(false);
        }
    };

    if (loading) {
        return (
            <div className="bg-[#0a0a0a] min-h-screen text-white">
                <Header />
                <div className="max-w-[1200px] mx-auto px-6 py-32 text-center">
                    <Loader2 size={32} className="animate-spin mx-auto text-[#FF3B30]" />
                    <div className="font-mono text-xs uppercase tracking-[0.3em] text-white/50 mt-6">
                        MATCHING ATTORNEYS...
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="bg-[#0a0a0a] min-h-screen text-white">
            <Header />

            <div className="max-w-[1400px] mx-auto px-6 sm:px-10 py-10">
                <Link
                    to={`/hpm-analysis/${caseId}`}
                    className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/50 hover:text-white flex items-center gap-2 mb-6"
                    data-testid="back-to-analysis"
                >
                    <ArrowLeft size={14} /> BACK TO ANALYSIS
                </Link>

                <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/50 mb-2">
                    ATTORNEY ESCALATION
                </div>
                <h1 className="font-anton text-5xl sm:text-7xl uppercase leading-none mb-4">
                    Your <span className="text-[#FF3B30]">top {matches.length}</span> matches.
                </h1>
                {caseData && (
                    <p className="font-sans text-white/60 mb-8 max-w-3xl">
                        Ranked for case{" "}
                        <span className="text-white font-semibold">{caseData.case_title}</span> —{" "}
                        <span className="font-mono text-xs uppercase tracking-wider">
                            {caseData.case_type?.replace(/_/g, " ")} / {caseData.jurisdiction} /{" "}
                            {caseData.language}
                        </span>
                        . Specialists licensed in your jurisdiction with proven track records. Pro-bono availability flagged.
                    </p>
                )}

                {matches.length === 0 && (
                    <div className="border-2 border-dashed border-white/20 p-12 sm:p-16 text-center" data-testid="attorney-matches-empty">
                        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#FF3B30] mb-3">
                            ATTORNEY NETWORK · LAUNCHING SOON
                        </div>
                        <div className="font-anton text-3xl uppercase mb-3">
                            We're onboarding verified firms now.
                        </div>
                        <p className="font-sans text-white/70 max-w-xl mx-auto mb-6 leading-relaxed">
                            HPM only matches you with attorneys who have verified their license,
                            jurisdiction, and case track record. We refuse to display fictional
                            placeholders. If you need urgent legal help, email{" "}
                            <a className="text-white underline" href="mailto:hello@alphacovenantholdings.com">
                                hello@alphacovenantholdings.com
                            </a>{" "}
                            and we'll connect you directly with a vetted lawyer.
                        </p>
                        <div className="flex flex-wrap justify-center gap-3">
                            <Link to="/hpm-law-firms" className="btn-brutal btn-brutal-red text-[10px]">
                                ARE YOU A FIRM? JOIN NOW
                            </Link>
                            <Link to="/contact" className="btn-brutal text-[10px]">
                                REQUEST HUMAN HELP
                            </Link>
                        </div>
                    </div>
                )}

                <div
                    className="grid grid-cols-1 lg:grid-cols-2 gap-6"
                    data-testid="attorney-matches"
                >
                    {matches.map((m) => (
                        <AttorneyCard
                            key={m.attorney.attorney_id}
                            match={m}
                            onRequest={startRequest}
                            requesting={requesting && activeAttorney?.attorney_id === m.attorney.attorney_id}
                            requested={!!requested[m.attorney.attorney_id]}
                        />
                    ))}
                </div>
            </div>

            {/* Request Form Modal */}
            {showForm && activeAttorney && (
                <div
                    className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4"
                    data-testid="request-modal"
                >
                    <div className="bg-[#0a0a0a] border-2 border-white max-w-xl w-full">
                        <div className="border-b-2 border-white/20 p-4 flex justify-between items-center">
                            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/60">
                                REQUEST CONSULTATION
                            </div>
                            <button
                                onClick={() => setShowForm(false)}
                                className="font-mono text-xs hover:text-[#FF3B30]"
                                data-testid="close-modal"
                            >
                                ✕ CLOSE
                            </button>
                        </div>
                        <div className="p-6">
                            <div className="font-anton text-3xl uppercase mb-2">
                                {activeAttorney.name}
                            </div>
                            <div className="font-mono text-xs uppercase tracking-wider text-white/50 mb-6">
                                {activeAttorney.law_firm}
                            </div>

                            <div className="space-y-4">
                                <div>
                                    <label className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/60 block mb-2">
                                        YOUR NAME
                                    </label>
                                    <input
                                        value={form.user_name}
                                        onChange={(e) =>
                                            setForm((f) => ({ ...f, user_name: e.target.value }))
                                        }
                                        placeholder="Full name"
                                        className="w-full bg-[#111] border-2 border-white/20 px-4 py-3 font-sans text-white placeholder:text-white/30 focus:outline-none focus:border-white"
                                        data-testid="modal-input-name"
                                    />
                                </div>
                                <div>
                                    <label className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/60 block mb-2">
                                        EMAIL
                                    </label>
                                    <input
                                        type="email"
                                        value={form.user_email}
                                        onChange={(e) =>
                                            setForm((f) => ({ ...f, user_email: e.target.value }))
                                        }
                                        placeholder="you@example.com"
                                        className="w-full bg-[#111] border-2 border-white/20 px-4 py-3 font-sans text-white placeholder:text-white/30 focus:outline-none focus:border-white"
                                        data-testid="modal-input-email"
                                    />
                                </div>
                                <div>
                                    <label className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/60 block mb-2">
                                        PHONE (OPTIONAL)
                                    </label>
                                    <input
                                        value={form.user_phone}
                                        onChange={(e) =>
                                            setForm((f) => ({ ...f, user_phone: e.target.value }))
                                        }
                                        placeholder="+1 ..."
                                        className="w-full bg-[#111] border-2 border-white/20 px-4 py-3 font-sans text-white placeholder:text-white/30 focus:outline-none focus:border-white"
                                        data-testid="modal-input-phone"
                                    />
                                </div>
                                <div>
                                    <label className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/60 block mb-2">
                                        NOTES (OPTIONAL)
                                    </label>
                                    <textarea
                                        value={form.notes}
                                        onChange={(e) =>
                                            setForm((f) => ({ ...f, notes: e.target.value }))
                                        }
                                        rows={3}
                                        placeholder="Anything specific you want the attorney to know"
                                        className="w-full bg-[#111] border-2 border-white/20 px-4 py-3 font-sans text-white placeholder:text-white/30 focus:outline-none focus:border-white resize-none"
                                        data-testid="modal-input-notes"
                                    />
                                </div>
                            </div>

                            <div className="border-2 border-white/10 mt-5 p-3 font-mono text-[10px] uppercase tracking-[0.25em] text-white/50">
                                Your HPM analysis (strength score + bot reports) is shared with the attorney to evaluate.
                            </div>

                            <div className="mt-6 flex gap-3">
                                <button
                                    onClick={() => setShowForm(false)}
                                    className="btn-brutal"
                                    data-testid="modal-cancel"
                                >
                                    CANCEL
                                </button>
                                <button
                                    onClick={submitRequest}
                                    disabled={!form.user_name || !form.user_email || requesting}
                                    className="btn-brutal btn-brutal-red"
                                    data-testid="modal-submit"
                                >
                                    {requesting ? (
                                        <>
                                            <Loader2 size={14} className="animate-spin" /> SENDING...
                                        </>
                                    ) : (
                                        <>SEND REQUEST</>
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            <Footer disclaimer={meta?.disclaimer || ""} />
        </div>
    );
}
